// Columna de lista de un maestro-detalle con ciclo de vida: búsqueda y alta arriba, filtros y conteo
// abajo, y la tabla. Filtrar lo hace quien la monta.
import { useId, type ReactNode } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button, Select } from '@/kit';
import { DataTable, UnavailableState, type DataTableProps } from '@/shared/patterns/DataTable';
import { FieldHelp } from '@/shared/patterns/EditorForm';
import { LIFECYCLE_LIST_HELP } from './helpText';
import { LIFECYCLE_STATUS_LABEL, type LifecycleStatus } from './lifecycleStatus';

/** Una opción del filtro de estado: un estado común, o una propia con su etiqueta. */
export type LifecycleStatusFilterOption =
    | { value: string; status: LifecycleStatus }
    | { value: string; label: string };

export interface LifecycleListFilter {
    /** Nombre accesible del combo. */
    label: string;
    /** Qué filtra; va en su «?». */
    help: string;
    value: string;
    onChange: (value: string) => void;
    options: readonly { value: string; label: string }[];
}

export interface LifecycleListPanelProps<TRow, TSortKey extends string = string> {
    search?: {
        value: string;
        onChange: (value: string) => void;
        label: string;
        /** Qué busca; sin ella, la ayuda común de búsqueda por nombre. */
        help?: string;
    };
    status?: {
        value: string;
        onChange: (value: string) => void;
        /** El valor que no filtra; va primero como «Todos los estados». */
        anyValue: string;
        options: readonly LifecycleStatusFilterOption[];
    };
    /** Filtros propios del dominio, después del de estado. */
    filters?: readonly LifecycleListFilter[];
    /** Cuántas filas se ven y cómo se nombran. */
    count: { value: number; one: string; many: string };
    create?: { label: string; onClick: () => void } | null;
    /** Aviso sobre la tabla, p. ej. que la lectura vino recortada. */
    notice?: ReactNode;
    /** Bajo la tabla: «Cargar más», una nota o el aviso de falla. */
    footer?: ReactNode;
    /** Con error la tabla no se pinta. */
    error?: { title: string; body?: string; onRetry: () => void } | null;
    table: DataTableProps<TRow, TSortKey>;
}

const SELECT_CLASS = 'h-7 w-full min-w-0 text-[11px]';

export function LifecycleListPanel<TRow, TSortKey extends string = string>({
    search,
    status,
    filters = [],
    count,
    create,
    notice,
    footer,
    error,
    table,
}: LifecycleListPanelProps<TRow, TSortKey>) {
    const searchId = useId();
    const allFilters: LifecycleListFilter[] = [
        ...(status
            ? [
                  {
                      label: 'Estado',
                      help: LIFECYCLE_LIST_HELP.status,
                      value: status.value,
                      onChange: status.onChange,
                      options: [
                          { value: status.anyValue, label: 'Todos los estados' },
                          ...status.options.map((option) => ({
                              value: option.value,
                              label:
                                  'status' in option
                                      ? LIFECYCLE_STATUS_LABEL[option.status]
                                      : option.label,
                          })),
                      ],
                  },
              ]
            : []),
        ...filters,
    ];

    return (
        <>
            <div className="flex flex-none flex-col gap-2 border-b border-border bg-muted/25 px-3 py-2">
                {search || create ? (
                    <div data-row="search" className="flex items-center gap-2">
                        {search ? (
                            <span
                                data-search
                                className="relative flex min-w-[200px] flex-1 items-center gap-1"
                            >
                                <label htmlFor={searchId} className="sr-only">
                                    {search.label}
                                </label>
                                <Search
                                    aria-hidden
                                    className="pointer-events-none absolute left-2 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                                />
                                <input
                                    id={searchId}
                                    type="search"
                                    placeholder={search.label}
                                    value={search.value}
                                    onChange={(event) => {
                                        search.onChange(event.target.value);
                                    }}
                                    className="h-8 w-full min-w-0 rounded-md border border-input bg-background pl-6 pr-2 text-[11px] outline-none focus-visible:border-ring"
                                />
                                <FieldHelp
                                    label={search.label}
                                    help={search.help ?? LIFECYCLE_LIST_HELP.search}
                                />
                            </span>
                        ) : (
                            <span className="flex-1" />
                        )}
                        {create ? (
                            <Button size="sm" className="flex-none" onClick={create.onClick}>
                                <Plus aria-hidden className="size-3.5" />
                                {create.label}
                            </Button>
                        ) : null}
                    </div>
                ) : null}
                <div data-row="filters" className="flex flex-wrap items-center gap-2">
                    {allFilters.map((filter) => (
                        <FilterSelect key={filter.label} filter={filter} />
                    ))}
                    <span className="ml-auto flex-none text-[10.5px] text-muted-foreground">
                        {count.value === 1
                            ? `1 ${count.one}`
                            : `${String(count.value)} ${count.many}`}
                    </span>
                </div>
            </div>
            {notice}
            <div className="flex min-h-0 flex-1 flex-col">
                {error ? (
                    <UnavailableState
                        title={error.title}
                        body={error.body ?? ''}
                        action={
                            <Button variant="outline" size="sm" onClick={error.onRetry}>
                                Reintentar
                            </Button>
                        }
                    />
                ) : (
                    <DataTable {...table} />
                )}
                {footer}
            </div>
        </>
    );
}

function FilterSelect({ filter }: { filter: LifecycleListFilter }) {
    const id = useId();
    return (
        <span data-filter className="flex min-w-[140px] flex-1 items-center gap-1">
            <label htmlFor={id} className="sr-only">
                {filter.label}
            </label>
            <Select
                id={id}
                className={SELECT_CLASS}
                value={filter.value}
                onChange={filter.onChange}
                options={[...filter.options]}
            />
            <FieldHelp label={filter.label} help={filter.help} />
        </span>
    );
}
